import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function ConvatecPatientCarePage() {
  useSEO({
    title: 'Convatec Patient Care Innovation | Design For',
    description: 'Designing patient care solutions for medical device users. Strategic consulting for healthcare product development and patient support.',
    keywords: 'patient care, medical devices, healthcare innovation, patient support, product strategy',
    canonical: '/work/convatec-patient-care',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Designing patient care solutions for medical device users',
    description: 'We partnered with Convatec to develop patient care solutions and support programmes for medical device users, improving patient outcomes and quality of life.',
    url: 'https://example.com/work/convatec-patient-care',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Medical device company - Patient care innovation',
    keywords: 'patient care, medical devices, healthcare innovation, patient support, product strategy',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/work"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Transforming the patient care experience across every phase
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">ConvaTec</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                ConvaTec is transforming how it supports ostomy, continence,
                and advanced wound care patients, alongside carers, nurses,
                doctors, and hospital procurement teams. The challenge was the
                sheer complexity of the care ecosystem: multiple patient needs,
                fragmented services, and a lack of shared clarity on where the
                biggest opportunities for improvement sat.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                The organisation needed a clear, evidence based view of the end
                to end care experience and a practical way to translate insight
                into action. Working closely with senior leaders and global
                teams, we explored patient, clinician, and business needs in
                parallel and turned this complexity into a focused,
                executable roadmap.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Delivered current and future state experience maps across key patient and clinician journeys',
                  'Identified 25 product and service opportunities across 8 strategic workstreams',
                  'Created a 3 year experience roadmap, aligned to technology and capability needs',
                  'Defined a 3 year vision supported by 4 clear strategic recommendations',
                  'Informed delivery of a new website and brand, grounded in real care needs',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-base text-gray-700 leading-relaxed"
                  >
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/7267f85f5ccc07f058256cd1cf369acd.jpeg"
                  alt="ConvaTec patient care experience map"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/409f0bfeb189d883dcf3269eb6b45b22.jpeg"
                  alt="ConvaTec patient care journey"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="convatec-patient-care" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">
                Strategic consulting for ambitious businesses.
              </p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">
                Navigate
              </h4>
              <div className="space-y-3">
                <Link
                  to="/"
                  className="block text-white/70 hover:text-primary transition-colors text-sm font-medium"
                >
                  Home
                </Link>
                <Link
                  to="/about"
                  className="block text-white/70 hover:text-primary transition-colors text-sm font-medium"
                >
                  About
                </Link>
                <Link
                  to="/services"
                  className="block text-white/70 hover:text-primary transition-colors text-sm font-medium"
                >
                  Services
                </Link>
                <Link
                  to="/work"
                  className="block text-white/70 hover:text-primary transition-colors text-sm font-medium"
                >
                  Work
                </Link>
                <Link
                  to="/resources"
                  className="block text-white/70 hover:text-primary transition-colors text-sm font-medium"
                >
                  Insights
                </Link>
                <Link
                  to="/contact"
                  className="block text-white/70 hover:text-primary transition-colors text-sm font-medium"
                >
                  Contact
                </Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">
                Follow
              </h4>
              <div className="flex gap-4">
                <a
                  href="https://www.linkedin.com/in/kara-prosser-26960977/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors"
                >
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">
              © 2026 Design For Consulting
            </p>
            <div className="flex gap-6">
              <a
                href="#"
                className="text-white/40 hover:text-primary transition-colors text-sm font-medium"
              >
                Privacy
              </a>
              <a
                href="#"
                className="text-white/40 hover:text-primary transition-colors text-sm font-medium"
              >
                Terms
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
